import { msg } from 'gt-react';
import {
  AS400_EXPORT_GROUP_ORDER,
  AS400_EXPORT_GROUP_SECTIONS,
  type As400ExportGroup,
  type As400ViewGroup,
} from '@erp/rpc';

// UI labels + button order for the two AS/400 group sets. The column sets
// themselves live in @erp/rpc (AS400_EXPORT_WORKSHEET_COLUMNS /
// AS400_VIEW_GROUP_COLUMNS), and so does the export worksheets' order; only the
// display labels and the view groups' ordering are UI-only.
// Kept out of the selector component files so those export components only
// (react-refresh / only-export-components).
//
// Both label maps are declared with `msg()` because they sit outside any
// component and so cannot call `useGT()`. Their values are encoded messages,
// not display text: the selector decodes them with the `m` from
// `useMessages()`. Nothing else may render them raw.

// The AS/400 item-master export file formats — the "Export" set on the review
// detail page's view selector.
export const AS400_EXPORT_GROUP_LABELS: Record<As400ExportGroup, string> = {
  ITEM: msg('Item', { context: 'AS/400 export worksheet name' }),
  PURCHASE_DATA: msg('Purchase Data', {
    context: 'AS/400 export worksheet name',
  }),
  PRICE: msg('Price', { context: 'AS/400 export worksheet name' }),
  ITEM_ADD: msg('Item Add', { context: 'AS/400 export worksheet name' }),
  // Rexel's tabs are "STOCK DATA WORK SHEET" and "STOCK ADD DATA WORKS SHEET":
  // a product's record at one stocking location, and its replenishment
  // settings there.
  STOCK: msg('Stock', { context: 'AS/400 export worksheet name' }),
  STOCK_ADD: msg('Stock Add', { context: 'AS/400 export worksheet name' }),
  // Rexel's tab is "WEB ITEM KEYWORD"; the per-language, per-line web product
  // keywords of an item.
  ITEM_KW: msg('Web Item Keyword', {
    context: 'AS/400 export worksheet name',
  }),
  MATRIX_CLASSIFICATION: msg('Matrix Classification', {
    context: 'AS/400 export worksheet name',
  }),
};

// The review page's worksheet order lives in the contract, so Review mode's
// list (sorted server-side) runs in the same order as the page's tabs.
export { AS400_EXPORT_GROUP_SECTIONS, AS400_EXPORT_GROUP_ORDER };

/**
 * Worksheets rows are added to freely: a product is stocked at whichever
 * locations the reviewer adds it at, whether or not the review has stock
 * records yet. So these never stand behind the "nothing under this worksheet"
 * empty state or grey a division out — the grid, and its Add row control, is
 * always there.
 */
export const AS400_FREE_ROW_GROUPS: ReadonlySet<As400ExportGroup> = new Set([
  'STOCK',
  'STOCK_ADD',
]);

// The "AS/400 View" column groups — the column narrowing on the Item Catalog
// search.
export const AS400_VIEW_GROUP_LABELS: Record<As400ViewGroup, string> = {
  // The AS/400 file's own name — a proper noun, left as it is.
  ITEMMAST5: msg('ITEMMAST5', {
    context: 'AS/400 file name, leave untranslated',
  }),
  ADDITIONAL_DESCRIPTION: msg('Additional Description', {
    context: 'AS/400 raw-data view name',
  }),
  GAINS_COST: msg('Gains Cost', {
    context:
      'AS/400 raw-data view name; GAINS is a vendor product name, leave untranslated',
  }),
  ASSOCIATED_ITEMS: msg('Associated Items', {
    context: 'AS/400 raw-data view name',
  }),
  QUANTITIES: msg('Quantities', { context: 'AS/400 raw-data view name' }),
};

export const AS400_VIEW_GROUP_ORDER: As400ViewGroup[] = [
  'ITEMMAST5',
  'ADDITIONAL_DESCRIPTION',
  'GAINS_COST',
  'ASSOCIATED_ITEMS',
  'QUANTITIES',
];

/** Which worksheet the review page shows. */
export type As400Selection = { kind: 'export'; group: As400ExportGroup };

/** The default selection: the first export worksheet. */
export const DEFAULT_AS400_SELECTION: As400Selection = {
  kind: 'export',
  group: AS400_EXPORT_GROUP_ORDER[0],
};
